import { useTranslations } from "use-intl";

import { Link, type LinkTarget } from "@/components/link";
import { OfferSummaryBadge, type OfferSummary } from "@/components/marketplace/offer-summary-badge";
import { Price } from "@/components/Price";
import type { OfferSummaryByCondition } from "@garimpa/lib/offerConditions";

export function CatalogOfferPrices({
  summary,
  destination,
}: {
  summary: OfferSummaryByCondition<OfferSummary>;
  destination: LinkTarget;
}) {
  const t = useTranslations("shared.affiliates");
  if (!summary.newOffer && !summary.usedOffer)
    return <p className="mt-2 text-xs text-muted-foreground">{t("noCurrentOffers")}</p>;
  return (
    <div className="relative z-20 mt-2 flex flex-col gap-1">
      {(["new", "used"] as const).map((condition) => {
        const offer = condition === "new" ? summary.newOffer : summary.usedOffer;
        if (!offer) return null;
        return (
          <Link
            key={condition}
            {...destination}
            className={
              condition === "new"
                ? "pointer-events-auto inline-flex w-full items-baseline gap-2 rounded text-lg font-black text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-primary"
                : "pointer-events-auto w-fit rounded text-xs text-muted-foreground hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-primary"
            }
          >
            {t.rich("conditionPriceLabel", {
              condition,
              label: (chunks) => (
                <span className="text-xs font-normal text-muted-foreground">{chunks}</span>
              ),
              price: () => (
                <Price
                  amount={offer.bestOfferPrice}
                  currency={offer.currency}
                  plain={condition === "used"}
                />
              ),
            })}
            {condition === "new" && (
              <OfferSummaryBadge
                offerSummary={offer}
                variant="signal"
                className="ml-auto w-auto shrink-0 self-end"
              />
            )}
          </Link>
        );
      })}
    </div>
  );
}
